import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { parseError } from '@/lib/errors'
import { type NaicCompany, type NaicCompanyFieldErrors, useSaveNaicCompany } from '@/models/naicCompany'

interface NaicCompanyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The company to edit, or null to add one. */
  company: NaicCompany | null
}

function fieldErrorsOf(error: unknown): NaicCompanyFieldErrors | undefined {
  return (error as { response?: { data?: NaicCompanyFieldErrors } }).response?.data
}

export function NaicCompanyDialog({ open, onOpenChange, company }: NaicCompanyDialogProps) {
  const saveCompany = useSaveNaicCompany()
  const [naicCode, setNaicCode] = useState('')
  const [name, setName] = useState('')
  const [fieldErrors, setFieldErrors] = useState<NaicCompanyFieldErrors>({})

  useEffect(() => {
    if (!open) return
    setNaicCode(company?.naicCode ?? '')
    setName(company?.name ?? '')
    setFieldErrors({})
  }, [open, company])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setFieldErrors({})
    try {
      await saveCompany.mutateAsync({ id: company?.id, naicCode, name })
      toast.success(company ? 'NAIC company updated' : 'NAIC company added')
      onOpenChange(false)
    } catch (error: unknown) {
      const errors = fieldErrorsOf(error)
      if (errors?.naic_code || errors?.name) {
        setFieldErrors(errors)
        return
      }
      toast.error(parseError(error))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{company ? 'Edit NAIC company' : 'Add NAIC company'}</DialogTitle>
            <DialogDescription>
              Certificates look up an insurer&apos;s NAIC code by the name its policy prints. A change reaches
              a customer&apos;s certificate when it&apos;s next generated.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="naic-company-name">Name</Label>
              <Input
                id="naic-company-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                aria-invalid={!!fieldErrors.name}
                required
              />
              <FieldError message={fieldErrors.name?.[0]} />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="naic-company-code">NAIC code</Label>
              <Input
                id="naic-company-code"
                value={naicCode}
                onChange={(event) => setNaicCode(event.target.value)}
                inputMode="numeric"
                maxLength={5}
                placeholder="12345"
                className="max-w-32"
                aria-invalid={!!fieldErrors.naic_code}
                required
              />
              <FieldError message={fieldErrors.naic_code?.[0]} />
            </div>
          </div>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!name.trim() || !naicCode.trim()}
              loading={saveCompany.isPending ? 'Saving…' : undefined}
            >
              {company ? 'Save changes' : 'Add company'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
